import React, { useState, useEffect, useRef } from "react";

/**
 * FloatingPuzzlePieces
 *
 * A page-level decorative layer that renders a few brand-colored puzzle pieces
 * which drift slowly across the background and gently parallax with scroll.
 * Pointer-events disabled so it never blocks interaction.
 */

const PUZZLE_PATH =
  "M2,8 C2,4.7 4.7,2 8,2 L17,2 C17.5,2 18,1.4 18,0.8 C18,-2 22,-2 22,0.8 C22,1.4 22.5,2 23,2 L32,2 C35.3,2 38,4.7 38,8 L38,17 C38,17.5 38.6,18 39.2,18 C42,18 42,22 39.2,22 C38.6,22 38,22.5 38,23 L38,32 C38,35.3 35.3,38 32,38 L23,38 C22.5,38 22,38.6 22,39.2 C22,42 18,42 18,39.2 C18,38.6 17.5,38 17,38 L8,38 C4.7,38 2,35.3 2,32 L2,23 C2,22.5 1.4,22 0.8,22 C-2,22 -2,18 0.8,18 C1.4,18 2,17.5 2,17 Z";

const PIECES = [
  { color: "#2196F3", x: "8%",  y: "16%", size: 64, rot: -18, speed: 0.06, opacity: 0.18 },
  { color: "#FFB300", x: "84%", y: "12%", size: 52, rot: 22,  speed: 0.10, opacity: 0.22 },
  { color: "#E53935", x: "92%", y: "70%", size: 70, rot: -30, speed: 0.04, opacity: 0.16 },
  { color: "#4CAF50", x: "5%",  y: "78%", size: 58, rot: 14,  speed: 0.08, opacity: 0.20 },
  { color: "#2196F3", x: "50%", y: "92%", size: 44, rot: 40,  speed: 0.05, opacity: 0.15 },
];

export default function FloatingPuzzlePieces({ className = "" }) {
  const [y, setY] = useState(0);
  const raf = useRef(null);

  useEffect(() => {
    const onScroll = () => {
      if (raf.current) return;
      raf.current = requestAnimationFrame(() => {
        setY(window.scrollY);
        raf.current = null;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <div
      aria-hidden
      className={`pointer-events-none absolute inset-0 overflow-hidden ${className}`}
      data-testid="floating-puzzle-pieces"
    >
      {PIECES.map((p, i) => (
        <svg
          key={i}
          width={p.size}
          height={p.size}
          viewBox="0 0 40 40"
          style={{
            position: "absolute",
            left: p.x,
            top: p.y,
            opacity: p.opacity,
            transform: `translateY(${-y * p.speed}px) rotate(${p.rot + y * p.speed * 0.6}deg)`,
            transition: "transform 90ms linear",
            filter: "drop-shadow(0 6px 14px rgba(31,41,55,0.10))",
          }}
        >
          <path d={PUZZLE_PATH} fill={p.color} />
        </svg>
      ))}
    </div>
  );
}
